<script setup lang="ts">
//#region imports
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import numeral from 'numeral';
import { useFormatters } from '@/composables/useFormatters';
import type { EmitsToListeners } from '@/types/core';
import type { UiDetailsTree } from '@/components/UIElements/UiDetailsTree.vue';
import type { GetCpaConversionsResponse } from '@/types/openapi-ts/types.gen';
//#endregion

//#region types
type ConversionState = NonNullable<GetCpaConversionsResponse['payload'][number]['current_state']>[number];
type ConditionsKey = keyof NonNullable<ConversionState['conditions']>;
type ConditionValueType = 'int' | 'currency' | 'time';
type CondValue = {
  min_value?: number | null;
  max_value?: number | null;
  current_value?: number | null;
};

interface SectionRow {
  key: ConditionsKey;
  label: string;
  type: ConditionValueType;
}

export interface CpaConversionDetailsParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}

export interface Props {
  conversion?: GetCpaConversionsResponse['payload'][number]['current_state'] | null;
}

export interface Emits {
  close: [];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  conversion: null,
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
const { formatCurrencyValue, formatDuration, formatValuePerDays } = useFormatters();
//#endregion

//#region state
const isVisible = ref(false);
//#endregion

//#region computed
const sectionsTemplate = computed<Array<{ title: string; rows: SectionRow[] }>>(() => [
  {
    title: t('payments.cpa_conversions.details.metrics.deposits'),
    rows: [
      {
        key: 'deposits_count',
        label: t('payments.cpa_conversions.details.metrics.deposits_count'),
        type: 'int',
      },
      {
        key: 'deposits_sum',
        label: t('payments.cpa_conversions.details.metrics.deposits_sum'),
        type: 'currency',
      },
    ],
  },
  {
    title: t('payments.cpa_conversions.details.metrics.sport_bets'),
    rows: [
      {
        key: 'sport_bets_count',
        label: t('payments.cpa_conversions.details.metrics.bets_count'),
        type: 'int',
      },
      {
        key: 'sport_bets_sum',
        label: t('payments.cpa_conversions.details.metrics.bets_sum'),
        type: 'currency',
      },
    ],
  },
  {
    title: t('payments.cpa_conversions.details.metrics.casino_bets'),
    rows: [
      {
        key: 'casino_bets_count',
        label: t('payments.cpa_conversions.details.metrics.bets_count'),
        type: 'int',
      },
      {
        key: 'casino_bets_sum',
        label: t('payments.cpa_conversions.details.metrics.bets_sum'),
        type: 'currency',
      },
    ],
  },
  {
    title: t('payments.cpa_conversions.details.metrics.player_activity'),
    rows: [
      {
        key: 'player_activity',
        label: t('payments.cpa_conversions.details.metrics.min_interval'),
        type: 'time',
      },
    ],
  },
  {
    title: t('payments.cpa_conversions.details.metrics.player_registration'),
    rows: [
      {
        key: 'player_registration',
        label: t('payments.cpa_conversions.details.metrics.player_reg'),
        type: 'int',
      },
    ],
  },
]);

const blocks = computed<UiDetailsTree[]>(() => {
  const states = props.conversion;

  if (!states || !Array.isArray(states)) {
    return [];
  }

  return states.map((state: ConversionState) => {
    const rootRows = [];

    if (isDefined(state.amount)) {
      rootRows.push({
        cols: [
          t('payments.cpa_conversions.details.metrics.payout'),
          formatCurrencyValue(state.currency_code, state.amount ?? null),
        ],
      });
    }

    if (isDefined(state.cap_players_count) && isDefined(state.cap_period_seconds)) {
      rootRows.push({
        cols: [
          t('payments.cpa_conversions.details.metrics.cap_players'),
          state.cap_players_count === 0 ? '0' : getCapPlayersText(state),
        ],
      });
    }

    if (isDefined(state.hold_seconds)) {
      rootRows.push({
        cols: [
          t('payments.cpa_conversions.details.metrics.hold'),
          formatDuration(state.hold_seconds ?? null),
        ],
      });
    }

    const sections = sectionsTemplate.value.map((section) => {
      const entries = section.rows.flatMap((row) => buildRowEntries(state, row));

      return {
        title: section.title,
        header: [
          section.title,
          t('payments.cpa_conversions.details.req'),
          t('payments.cpa_conversions.details.fact'),
        ],
        rows: entries.map((entry) => ({
          cols: [entry.label, entry.req, entry.fact],
        })),
        visible: entries.length > 0,
      };
    });

    return {
      rootTitle: 'CPA',
      rootRows,
      sections,
      rootColumnsWeight: [1, 1],
    };
  });
});

//#endregion

//#region methods
function isDefined(value: unknown) {
  return value !== null && value !== undefined;
}

function getCond(state: ConversionState, key: ConditionsKey) {
  const condition = state?.conditions?.[key] ?? {};
  const { min_value = null, max_value = null, current_value = null } = condition as CondValue;

  return { min: min_value, max: max_value, fact: current_value };
}

function getCapPlayersText(state: ConversionState) {
  if (state.cap_players_count === 0) {
    return '0';
  }

  const days = formatValuePerDays(state.cap_period_seconds);

  return t(
    'payments.cpa_conversions.details.cap_players_per_days',
    { playersCount: state.cap_players_count, days, count: days },
  );
}

function buildRowEntries(
  state: ConversionState,
  options: {
    key: ConditionsKey;
    label: string;
    type: ConditionValueType;
  },
) {
  const { key, label, type } = options;

  if (key === 'player_activity') {
    const raw = state.conditions?.player_activity;
    const activity = raw ?? ({} as { min_interval?: number; current_value?: number; interval_unit?: 'hours' | 'days' });
    if (activity.min_interval === undefined && activity.current_value === undefined) {
      return [];
    }

    return [
      {
        label,
        req: t(`affiliates.time.${activity.interval_unit}`, { count: activity.min_interval ?? 0 }, activity.min_interval ?? 0),
        fact: t(`affiliates.time.${activity.interval_unit}`, { count: activity.current_value ?? 0 }, activity.current_value ?? 0),
      },
    ];
  }

  const { min, max, fact } = getCond(state, key);

  if (min === null && max === null) {
    return [];
  }

  const formatValue = (value: number | null) => {
    if (value === null) {
      return '';
    }

    if (type === 'currency') {
      return formatCurrencyValue(state.currency_code, value);
    }

    if (type === 'time') {
      return formatDuration(value);
    }

    return numeral(value).format('0,0');
  };

  const rows: Array<{ label: string; req: string; fact: string }> = [];

  if (max !== null) {
    rows.push({
      label: `${label} ${t('payments.cpa_conversions.details.max')}`,
      req: formatValue(max),
      fact: formatValue(fact),
    });
  }

  if (min !== null) {
    rows.push({
      label: `${label} ${t('payments.cpa_conversions.details.min')}`,
      req: formatValue(min),
      fact: formatValue(fact),
    });
  }

  return rows;
}

const open = () => {
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
};
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="$t('payments.cpa_conversions.details.title')"
    :width="715"
    @close="handleClose"
  >
    <template v-slot:body>
      <UiDetailsTree :blocks="blocks" />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.close') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
